import React, { useEffect, useState, useRef } from "react"
import { db } from "../../../utils/firebase"
import firebase from "firebase"
import { enquiryMatchesSearch } from "../../../utils/enquiries/search"

/**
 * UI components
 */
import Table from "../../../components/structure/table/table"
import Enquiry from "../../../components/enquiry/enquiry"
import Button from "../../../components/ui/button/button"
import Checkbox from "../../../components/ui/checkbox/checkbox"

/**
 * Loading icon for when the button is processing
 */
import { LoadingIcon } from "../../../utils/svgs"

const REQUIRE_ATTENTION_SORT_STORAGE_KEY = "enquiries.requireAttentionSort"

const readStoredSort = () => {
	try {
		const raw = window.localStorage.getItem(REQUIRE_ATTENTION_SORT_STORAGE_KEY)
		if (!raw) return { key: "", direction: "" }
		const parsed = JSON.parse(raw)
		if (parsed && typeof parsed.key === "string" && typeof parsed.direction === "string") {
			return parsed
		}
	} catch (err) {
		// ignore parse/storage errors and fall back to default
	}
	return { key: "", direction: "" }
}

const writeStoredSort = (value) => {
	try {
		window.localStorage.setItem(REQUIRE_ATTENTION_SORT_STORAGE_KEY, JSON.stringify(value))
	} catch (err) {
		// ignore storage errors
	}
}

/**
 * Functional component returning all the leads that are found with the status of "REQUIRE_ATTENTION"
 */
export default function RequireAttention(props) {
	const { maskDetails, searchTerm, setAssignEnquiry, setBulk, showEnquiryWindow, startBulkAssign, updateRequiredCount } = props
	const [loading, setLoading] = useState(false)
	const [enquiries, setEnquiries] = useState([])
	const [bulkAssign, setBulkAssign] = useState([])
	const [sort, setSort] = useState(() => readStoredSort())

	useEffect(() => {
		writeStoredSort(sort)
	}, [sort])
	const [clientsById, setClientsById] = useState({})
	const [priorByEnquiryId, setPriorByEnquiryId] = useState({})
	const [usersById, setUsersById] = useState({})
	const [leaderboardAgentIds, setLeaderboardAgentIds] = useState({})
	const latestSnapshotRef = useRef(0)

	/**
	 * When the local bulk assign array is updated
	 */
	useEffect(() => {
		/**
		 * Push the updated array up the props
		 */
		setBulk(bulkAssign)
	}, [bulkAssign, setBulk])

	/**
	 * On component load
	 */
	useEffect(() => {
		let isMounted = true

		/**
		 * Pull agents that are currently on the leaderboard
		 */
		db.collection("users")
			.where("show_for.targets", "==", true)
			.get()
			.then((usersSnap) => {
				if (!isMounted) return

				const leaderboardUsers = {}
				usersSnap.forEach((doc) => {
					leaderboardUsers[doc.id] = true
				})
				setLeaderboardAgentIds(leaderboardUsers)
			})

		/**
		 * Show a loading spinner over the top
		 */
		setLoading(true)
		/**
		 * Setup a listener on the databse for the enquiries
		 */
		const unsubscribe = db
			.collection("enquiries")
			.orderBy("created")
			.where("status", "==", "REQUIRE_ATTENTION")
			.onSnapshot((enquiriesSnap) => {
				const snapshotId = ++latestSnapshotRef.current
				/**
				 * Map the enquiries found into the local state in one pass
				 */
				const nextEnquiries = enquiriesSnap.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
				}))
				setEnquiries(nextEnquiries)
				/**
				 * And then set the loading state back to false to show the enquiries
				 */
				setLoading(false)

				/**
				 * Fetch related data for clients and prior enquiries
				 */
				const hydrateRelatedData = async () => {
					const clientIds = Array.from(
						new Set(
							nextEnquiries
								.map((enquiry) => enquiry.client)
								.filter(Boolean)
						)
					)

					if (clientIds.length === 0) {
						if (!isMounted || latestSnapshotRef.current !== snapshotId) return
						setClientsById({})
						setPriorByEnquiryId({})
						setUsersById({})
						return
					}

					/**
					 * Batch fetch client records
					 */
					const clientsData = {}
					const batchSize = 10
					for (let i = 0; i < clientIds.length; i += batchSize) {
						const batch = clientIds.slice(i, i + batchSize)
						const clientDocs = await db
							.collection("clients")
							.where(firebase.firestore.FieldPath.documentId(), "in", batch)
							.get()

						clientDocs.forEach((doc) => {
							clientsData[doc.id] = doc.data()
						})
					}

					/**
					 * Batch fetch all enquiries for these clients to determine prior enquiries
					 */
					const enquiriesByClient = {}
					for (let i = 0; i < clientIds.length; i += batchSize) {
						const batch = clientIds.slice(i, i + batchSize)
						const enquiryDocs = await db
							.collection("enquiries")
							.where("client", "in", batch)
							.get()

						enquiryDocs.forEach((doc) => {
							const data = doc.data()
							const clientId = data.client
							if (!enquiriesByClient[clientId]) {
								enquiriesByClient[clientId] = []
							}
							enquiriesByClient[clientId].push({
								id: doc.id,
								...data,
							})
						})

						const removedEnquiryDocs = await db
							.collection("enquiries_removed")
							.where("enquiry.client", "in", batch)
							.get()

						removedEnquiryDocs.forEach((doc) => {
							const data = doc.data()
							const baseEnquiry = data?.enquiry || {}
							const clientId = baseEnquiry.client
							if (!clientId) return

							if (!enquiriesByClient[clientId]) {
								enquiriesByClient[clientId] = []
							}
							enquiriesByClient[clientId].push({
								id: doc.id,
								...baseEnquiry,
							})
						})
					}

					const getCreatedSeconds = (enquiry) => enquiry.created?._seconds || enquiry.created?.seconds || 0
					const priorMap = {}
					const priorAgentIds = new Set()

					Object.keys(enquiriesByClient).forEach((clientId) => {
						const sorted = enquiriesByClient[clientId].sort((a, b) => getCreatedSeconds(a) - getCreatedSeconds(b))
						sorted.forEach((enquiry, index) => {
							const priorEnquiry = index > 0 ? sorted[index - 1] : null
							priorMap[enquiry.id] = priorEnquiry
							if (priorEnquiry?.agent) {
								priorAgentIds.add(priorEnquiry.agent)
							}
						})
					})

					/**
					 * Pull user data for any prior agents found
					 */
					const usersData = {}
					const priorAgentIdList = Array.from(priorAgentIds)
					for (let i = 0; i < priorAgentIdList.length; i += batchSize) {
						const batch = priorAgentIdList.slice(i, i + batchSize)
						const userDocs = await db
							.collection("users")
							.where(firebase.firestore.FieldPath.documentId(), "in", batch)
							.get()

						userDocs.forEach((doc) => {
							usersData[doc.id] = doc.data()
						})
					}

					if (!isMounted || latestSnapshotRef.current !== snapshotId) return

					setClientsById(clientsData)
					setPriorByEnquiryId(priorMap)
					setUsersById(usersData)
				}

				hydrateRelatedData().catch(() => null)
			})
		/**
		 * On component unload
		 */
		return () => {
			isMounted = false
			unsubscribe()
		}
	}, [updateRequiredCount])

	/**
	 * Toggle a single enquiry in or out of the bulk-assign selection
	 */
	const toggleSelect = (enquiryID) => {
		setBulkAssign((current) =>
			current.includes(enquiryID)
				? current.filter((enquiry) => enquiry !== enquiryID)
				: [...current, enquiryID]
		)
	}

	/**
	 * Keep the selection honest: whenever the live enquiry list changes (e.g. an
	 * enquiry gets assigned and leaves REQUIRE_ATTENTION), drop any selected IDs
	 * that are no longer present. This also clears the selection automatically
	 * after a bulk assign completes.
	 */
	useEffect(() => {
		setBulkAssign((current) => {
			if (current.length === 0) return current
			const presentIds = new Set(enquiries.map((enquiry) => enquiry.id))
			const next = current.filter((id) => presentIds.has(id))
			return next.length === current.length ? current : next
		})
	}, [enquiries])

	const nextSortState = (currentSort, key) => {
		if (currentSort.key !== key) return { key, direction: "asc" }
		if (currentSort.direction === "asc") return { key, direction: "desc" }
		return { key: "", direction: "" }
	}

	const getSeconds = (timestamp) => timestamp?._seconds || timestamp?.seconds || 0
	const compareValues = (a, b) => {
		if (typeof a === "number" || typeof b === "number") return (a || 0) - (b || 0)
		return String(a || "").localeCompare(String(b || ""), undefined, { numeric: true, sensitivity: "base" })
	}
	const getSortValue = (enquiry, key) => {
		const client = clientsById[enquiry.client] || {}
		const priorEnquiry = priorByEnquiryId[enquiry.id]
		const priorAgentId = priorEnquiry?.status === "REQUIRE_ATTENTION" && !priorEnquiry?.agent ? null : priorEnquiry?.agent || null
		const priorUser = priorAgentId ? usersById[priorAgentId] : null
		switch (key) {
			case "site":
				return enquiry.site
			case "enquired":
				return getSeconds(enquiry.created)
			case "name":
				return `${client.first_name || ""} ${client.last_name || ""}`.trim()
			case "location":
				return enquiry.location
			case "date_from":
				return getSeconds(enquiry.date_from)
			case "group_size":
				return enquiry.group_size
			case "prior":
				return priorUser ? `${priorUser.first_name || ""} ${priorUser.last_name || ""}`.trim() : ""
			default:
				return ""
		}
	}
	const filteredEnquiries = enquiries.filter((enquiry) =>
		enquiryMatchesSearch(enquiry, clientsById[enquiry.client], searchTerm)
	)
	const sortedEnquiries = sort.key && sort.direction
		? [...filteredEnquiries].sort((a, b) => {
			const comparison = compareValues(getSortValue(a, sort.key), getSortValue(b, sort.key))
			return sort.direction === "asc" ? comparison : -comparison
		})
		: [...filteredEnquiries].sort((a, b) => getSeconds(a.created) - getSeconds(b.created))

	useEffect(() => {
		updateRequiredCount(filteredEnquiries.length)
	}, [filteredEnquiries.length, updateRequiredCount])

	/**
	 * Select-all only ever acts on the rows that are currently visible (after the
	 * search filter), so we never bulk-assign something that isn't on screen.
	 */
	const visibleIds = sortedEnquiries.map((enquiry) => enquiry.id)
	const selectedVisibleCount = visibleIds.filter((id) => bulkAssign.includes(id)).length
	const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length

	const toggleSelectAll = () => {
		if (allVisibleSelected) {
			/**
			 * Everything visible is selected, so clear the visible rows
			 */
			setBulkAssign((current) => current.filter((id) => !visibleIds.includes(id)))
		} else {
			/**
			 * Add every visible row to the selection (union, preserving any
			 * off-screen selections the user already made)
			 */
			setBulkAssign((current) => Array.from(new Set([...current, ...visibleIds])))
		}
	}

	const dataHeadings = [
		{ key: "site", label: "Site" },
		{ key: "enquired", label: "Enquired" },
		{ key: "name", label: "Name" },
		{ key: "location", label: "Location/Event" },
		{ key: "date_from", label: "Date from" },
		{ key: "group_size", label: "Group size" },
		{ key: "prior", label: "Prior enquiry" },
		{ key: "actions", label: "" },
	].map((heading) => ({
		...heading,
		sortable: heading.key !== "actions",
		active: sort.key === heading.key,
		direction: sort.key === heading.key ? sort.direction : "",
		onClick: () => setSort((currentSort) => nextSortState(currentSort, heading.key)),
	}))

	/**
	 * Leading checkbox column for selecting every visible row at once
	 */
	const selectAllHeading = {
		key: "select",
		sortable: false,
		label: (
			<Checkbox
				className="enquiry-select-all"
				checked={allVisibleSelected}
				onClick={toggleSelectAll}
			/>
		),
	}

	const headings = [selectAllHeading, ...dataHeadings]

	return (
		<>
			<div className={["enquiry-bulk-bar", bulkAssign.length === 0 && "is-disabled"].filter(Boolean).join(" ")}>
				<span className="enquiry-bulk-count">
					{bulkAssign.length} {bulkAssign.length === 1 ? "enquiry" : "enquiries"} selected
				</span>
				<div className="enquiry-bulk-actions">
					<Button
						small={true}
						type="OUTLINE"
						label="Clear selection"
						disabled={bulkAssign.length === 0}
						onClick={() => setBulkAssign([])}
					/>
					<Button
						small={true}
						label="Assign selected"
						disabled={bulkAssign.length === 0}
						onClick={() => startBulkAssign()}
					/>
				</div>
			</div>

			<Table
				className="enquiries-table"
				headings={headings}>
				{loading && (
					<div className="table-loading-splash">
						<div className="loading-wrapper">
							<p>Loading...</p>
							<div className="svg-loading-wrap">
								<LoadingIcon />
							</div>
						</div>
					</div>
				)}

				{/* Loop through any enquiries to print them into the table */}
				{sortedEnquiries.map((enquiry) => {
					const priorEnquiry = priorByEnquiryId[enquiry.id]
					const priorIsOpenUnassigned = priorEnquiry?.status === "REQUIRE_ATTENTION" && !priorEnquiry?.agent
					const priorAgentId = priorIsOpenUnassigned ? null : priorEnquiry?.agent || null
					const priorAgentUser = priorAgentId ? usersById[priorAgentId] : null
					const priorAgentOnLeaderboard = priorAgentId ? !!leaderboardAgentIds[priorAgentId] : false
					const hasPriorLoaded = Object.prototype.hasOwnProperty.call(priorByEnquiryId, enquiry.id)
					const hasPriorEnquiry = !!priorEnquiry && !priorIsOpenUnassigned

					return (
						<Enquiry
							key={enquiry.id}
							id={enquiry.id}
							details={enquiry}
							tab="REQUIRE_ATTENTION"
							maskDetails={maskDetails}
							clientData={clientsById[enquiry.client]}
							hasPriorEnquiry={hasPriorEnquiry}
							priorLoading={!hasPriorLoaded}
							priorAgentId={priorAgentId}
							priorAgentUser={priorAgentUser}
							priorAgentOnLeaderboard={priorAgentOnLeaderboard}
							selectable={true}
							isSelected={bulkAssign.includes(enquiry.id)}
							onToggleSelect={(enquiryID) => toggleSelect(enquiryID)}
							showAssignWindow={(enquiryID) => setAssignEnquiry(enquiryID)}
							showEnquiryWindow={(enquiryID) => showEnquiryWindow(enquiryID)}
						/>
					)
				})}

				{/* If there are no enquiries to show */}
				{filteredEnquiries.length === 0 && (
					<tr>
						<td colSpan={9}>
							<small>
								{searchTerm?.trim()
									? `No enquiries found for "${searchTerm.trim()}" in Require attention.`
									: "There are no enquiries that require attention at the moment"}
							</small>
						</td>
					</tr>
				)}
			</Table>
		</>
	)
}
